import { useCallback, useEffect, useState } from 'react';

import { getPodcastListAndEpisodes } from '../api/mobileApi';
import { useAppSelector } from '../app/hooks';
import { DataState } from '../constants';
import { selectBearerToken } from '../features/bearertoken/store/selectors';
import { PodcastEpisodeModel, PodcastModel } from '../models/Podcast.model';
import { logger } from '../helpers/logger';

interface UsePodcastListAndEpisodesReturn {
  podcastList: Partial<PodcastModel>[] | null;
  podcastListState: DataState;
  latestEpisodesList: PodcastEpisodeModel[] | null;
  latestEpisodesListState: DataState;
}

export const usePodcastListAndEpisodes = (
  _tabId: number,
  list: string[],
): UsePodcastListAndEpisodesReturn => {
  const bearerToken = useAppSelector(selectBearerToken);

  // local state management instead of redux store for more dynamic updates
  const [podcastList, setPodcastList] = useState<Partial<PodcastModel>[] | null>(null);
  const [latestEpisodesList, setLatestEpisodesList] = useState<
    PodcastEpisodeModel[] | null
  >(null);
  const [podcastListState, setPodcastListState] = useState<DataState>(
    DataState.NOT_LOADED,
  );
  const [latestEpisodesListState, setLatestEpisodesListState] = useState<DataState>(
    DataState.NOT_LOADED,
  );
  // track the current list to detect changes
  const [currentList, setCurrentList] = useState<string[]>([]);

  const fetchPodcastListAndEpisodesData = useCallback(async () => {
    // set loading state
    setPodcastListState(DataState.LOADING);
    setLatestEpisodesListState(DataState.LOADING);

    try {
      const result = await getPodcastListAndEpisodes(
        list,
        bearerToken,
        parseInt(process.env?.REACT_APP_APP_ID || '0', 10),
      );

      // update local state with fetched data
      setPodcastList(result.podcasts);
      setLatestEpisodesList(result.lastEpisodes);
      setPodcastListState(DataState.LOADED);
      setLatestEpisodesListState(DataState.LOADED);
    } catch (error) {
      // set error state
      setPodcastListState(DataState.ERROR);
      setLatestEpisodesListState(DataState.ERROR);
      logger.error('[Podcasts]', 'Failed to fetch podcast list and episodes:', error);
    }
  }, [list, bearerToken]);

  useEffect(() => {
    // check if list has changed or if we need initial fetch
    const listChanged = JSON.stringify(currentList) !== JSON.stringify(list);
    const needsInitialFetch = podcastListState === DataState.NOT_LOADED;

    if (list && list.length > 0 && bearerToken && (needsInitialFetch || listChanged)) {
      setCurrentList(list);
      fetchPodcastListAndEpisodesData();
    }
  }, [list, bearerToken, podcastListState, fetchPodcastListAndEpisodesData, currentList]);

  return {
    podcastList,
    podcastListState,
    latestEpisodesList,
    latestEpisodesListState,
  };
};
